import { useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate } from '@/lib/dates'
import { useEmailPreview } from '@/queries/emailLog'
import type { EmailLogDetail, EmailPreview } from '@/types/emailLog'

// Defence in depth over the server-side sanitising: the frame gets no scripts
// and no same-origin access (sandbox without allow-scripts), and its own CSP
// allows images only from data: unless remote images were asked for, so
// nothing in a preview can load a tracker or reach the app.
function frameDoc(html: string, remoteImages: boolean): string {
  const img = remoteImages ? 'data: https:' : 'data:'
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${img}; style-src 'unsafe-inline'"><base target="_blank"></head><body style="margin:0;padding:12px;font-family:-apple-system,Segoe UI,sans-serif">${html}</body></html>`
}

function unavailableCopy(data: EmailPreview, direction: EmailLogDetail['direction']): string {
  const verb = direction === 'inbound' ? 'received' : 'sent'
  if (data.reason === 'expired') return 'The stored copy of this email has expired (copies are kept for 90 days).'
  if (data.reason === 'unavailable') return 'Preview unavailable: email storage is not configured in this environment.'
  if (data.previewSince) return `No preview for this email. Copies are kept for emails ${verb} from ${formatDate(data.previewSince)}.`
  return `No preview for this email. It was ${verb} before copies were kept.`
}

const toggleClass = (active: boolean) =>
  `h-[var(--etn-control-height-sm)] rounded-etn-pill px-3 text-etn-control font-semibold transition-colors ${
    active ? 'bg-etn-accent-tint text-etn-accent-text-active' : 'text-etn-text-label hover:bg-etn-surface-sunken hover:text-etn-text'
  }`

export function EmailPreviewPane({ entry, enabled }: { entry: EmailLogDetail; enabled: boolean }) {
  const [recipientId, setRecipientId] = useState(entry.recipients[0]?._id ?? entry._id)
  const [format, setFormat] = useState<'html' | 'text'>('html')
  const [images, setImages] = useState(false)
  const { data, isLoading, error } = useEmailPreview(recipientId, images, enabled)

  const picker = entry.direction === 'outbound' && entry.recipients.length > 1 && (
    <select
      aria-label="Recipient"
      value={recipientId}
      onChange={e => setRecipientId(e.target.value)}
      className="h-[var(--etn-control-height-sm)] max-w-[220px] rounded-etn-pill border border-etn-border-input bg-etn-surface px-3 text-etn-control text-etn-text-cell"
    >
      {entry.recipients.map(r => (
        <option key={r._id} value={r._id}>{r.address}</option>
      ))}
    </select>
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Preview format" className="inline-flex gap-1">
          <button type="button" aria-pressed={format === 'html'} className={toggleClass(format === 'html')} onClick={() => setFormat('html')}>HTML</button>
          <button type="button" aria-pressed={format === 'text'} className={toggleClass(format === 'text')} onClick={() => setFormat('text')}>Text</button>
        </div>
        {picker}
        {format === 'html' && (data?.remoteImagesBlocked || images) && (
          <label className="ml-auto inline-flex items-center gap-1.5 text-etn-micro text-etn-text-label">
            <input type="checkbox" checked={images} onChange={e => setImages(e.target.checked)} />
            Show remote images
          </label>
        )}
      </div>

      {isLoading && <Skeleton className="h-64 w-full" />}
      {error && <p className="text-etn-control text-etn-danger-text">Couldn't load the preview.</p>}
      {data && !data.available && (
        <p className="rounded-etn-card bg-etn-surface-sunken px-3.5 py-3 text-etn-control text-etn-text-label">
          {unavailableCopy(data, entry.direction)}
        </p>
      )}
      {data?.available && format === 'html' && (
        data.html
          ? (
            <iframe
              title="Email preview"
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              srcDoc={frameDoc(data.html, images)}
              className="h-[50vh] w-full rounded-etn-card border border-etn-border-hairline bg-white"
            />
          )
          : <p className="text-etn-control text-etn-text-label">This email has no HTML part.</p>
      )}
      {data?.available && format === 'text' && (
        <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-etn-card border border-etn-border-hairline bg-etn-surface px-3.5 py-3 font-sans text-etn-control text-etn-text-cell">
          {data.text || '(no text part)'}
        </pre>
      )}
    </div>
  )
}
